import { snapchat } from '@/gen/proto/protos';
import IColourList = snapchat.bitmoji.api.IColourList;
import ITraitCategoryOption = snapchat.bitmoji.api.ITraitCategoryOption;
import { TraitColourPickerConfig } from '@/services/ColourPickerOptionsHelper';
import TraitCategory = snapchat.bitmoji.api.TraitCategory;

const COLOUR_PICKER_TRAIT_CATEGORIES: string[] = ['hat'];

const isColourPickerTraitCategory = (categoryKey: string) => {
  return COLOUR_PICKER_TRAIT_CATEGORIES.includes(categoryKey);
};

const getTraitColourKeyPrefix = (categoryKey: string) => `${categoryKey}_tone`;

/**
 * Constructs a collection of colour picker options for the specified option + category combination
 * or 'null' if none exists.
 *
 * @param categoryKey
 * @param optionValue
 * @param allCategories
 * @param allSwatchSets
 */
const colourPickerConfigForSelectedItem = (
  categoryKey: string,
  optionValue: number,
  allCategories: AllCategories[],
  allSwatchSets: Record<string, IColourList>,
): TraitColourPickerConfig | null => {
  // Sanity check category, selected option
  if (!isColourPickerTraitCategory(categoryKey) || optionValue === -1) {
    return null;
  }

  // Check full option metadata for colour palette details
  const currCategory = <TraitCategory | undefined>(
    allCategories.find((category) => category.key === categoryKey)
  );
  if (!currCategory || !currCategory.trait_category_options) {
    return null;
  }

  const selectedOptionDetails:
    | ITraitCategoryOption
    | undefined = currCategory.trait_category_options.find(
    (option) => option.value === optionValue,
  );
  if (!selectedOptionDetails || !selectedOptionDetails.color_palette_swatch_set_ids) {
    return null;
  }

  // Parse color palette metadata into colour picker options
  const currOptionSwatchSets = selectedOptionDetails.color_palette_swatch_set_ids
    .map((swatchSetId) => allSwatchSets[swatchSetId.toString()])
    .filter((swatchSet) => swatchSet && swatchSet.value && swatchSet.value.length !== 0)
    .map((swatchSet) => <number[]>swatchSet.value);

  // Ensure options are actually present
  if (currOptionSwatchSets.length === 0) {
    return null;
  }

  return new TraitColourPickerConfig(
    currOptionSwatchSets,
    selectedOptionDetails.secondary_colors || undefined,
    selectedOptionDetails.default_swatch_set_index || undefined,
  );
};

const getSelectedColourPickerOptionFromTraits = (
  categoryKey: string,
  selectedTraits: Record<string, number>,
): number[] | null => {
  const colorTraitPrefix = `${categoryKey}_tone`;

  // NOTE: This function assumes trait keys for colours are iterated in order (e.g. _tone1, _tone2, etc.)
  // At the moment, this is determined by "mapColourOptionToSelectedTraits()" but if that changes
  // such that keys are not added in order this may break
  return Object.entries(selectedTraits)
    .filter(([traitKey]) => traitKey.startsWith(colorTraitPrefix))
    .map(([, colourValue]) => colourValue);
};

/**
 * Determines the selected swatch set/colour picker option for the specified trait item. Returns
 * a default (currently the first option) if none was selected. Returns null if the specified
 * item has no colour options.
 */
const getSelectedColourPickerOptionOrDefaultForItem = (
  categoryKey: string,
  optionValue: number,
  allCategories: AllCategories[],
  allSwatchSets: Record<string, IColourList>,
  selectedColourSwatchSetsByTraitCategory: Record<string, Record<number, number[]>>,
): number[] | null => {
  const colourPickerOptions = colourPickerConfigForSelectedItem(
    categoryKey,
    optionValue,
    allCategories,
    allSwatchSets,
  );

  if (!colourPickerOptions) {
    return null;
  }

  // See if there was a previous selection for this option
  const selectedColourSwatchSetsForTraitCategory =
    selectedColourSwatchSetsByTraitCategory[categoryKey];
  const selectedColourOption =
    optionValue && selectedColourSwatchSetsForTraitCategory
      ? selectedColourSwatchSetsForTraitCategory[optionValue]
      : null;

  // If no prior selection, see if there's a default, if no default, fall back to the first option
  return (
    selectedColourOption ||
    colourPickerOptions.options[colourPickerOptions.defaultColourOptionIndex || 0]
  );
};

const mapColourOptionToSelectedTraits = (
  categoryKey: string,
  colourOption: number[],
): Record<string, number> => {
  const colourKeyPrefix = getTraitColourKeyPrefix(categoryKey);
  const result: Record<string, number> = {};
  colourOption.forEach((value, index) => {
    result[`${colourKeyPrefix}${index + 1}`] = value;
  });
  return result;
};

export default {
  COLOUR_PICKER_TRAIT_CATEGORIES,
  isColourPickerTraitCategory,
  getTraitColourKeyPrefix,
  colourPickerConfigForSelectedItem,
  getSelectedColourPickerOptionFromTraits,
  getSelectedColourPickerOptionOrDefaultForItem,
  mapColourOptionToSelectedTraits,
};
